import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

// Sprint 8.A.2 — Onboarding-Nudge nach erstem gespeicherten Flexpfad.
// Trigger: GET /api/cockpit/onboarding-nudge gibt show_nudge=true zurück.
// User wählt 1-3 Standard-Tags (oder „Später"). Tags via PATCH gespeichert.
// LocalStorage `onboarding_nudge_shown` → wird nur einmal angezeigt.

const STORAGE_KEY = "f4s_onboarding_nudge_shown_v1";

function isAlreadyShown() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch (_) {
    return false;
  }
}

function markShown() {
  try {
    localStorage.setItem(STORAGE_KEY, "true");
  } catch (_) {
    /* noop */
  }
}

export default function OnboardingNudge({ onSaved }) {
  const [state, setState] = useState({ ready: false, show: false, brueckeId: null, tags: [] });
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isAlreadyShown()) {
      setState({ ready: true, show: false, brueckeId: null, tags: [] });
      return;
    }
    let cancelled = false;
    api
      .get("/cockpit/onboarding-nudge")
      .then((res) => {
        if (cancelled) return;
        const d = res.data || {};
        setState({
          ready: true,
          show: !!d.show_nudge,
          brueckeId: d.bruecke_id || null,
          tags: Array.isArray(d.standard_tags) ? d.standard_tags : [],
        });
      })
      .catch(() => {
        if (!cancelled) setState({ ready: true, show: false, brueckeId: null, tags: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!state.ready || !state.show) return null;

  const togglePick = (key) => {
    setPicked((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= 3) return prev;
      return [...prev, key];
    });
  };

  const handleSkip = () => {
    markShown();
    setState((s) => ({ ...s, show: false }));
  };

  const handleSave = async () => {
    if (picked.length === 0) {
      handleSkip();
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/anliegen/${state.brueckeId}/update`, { tags: picked });
      markShown();
      setState((s) => ({ ...s, show: false }));
      if (onSaved) onSaved(picked);
    } catch (_e) {
      // Fail silently — Nudge nicht doppelt zeigen
      markShown();
      setState((s) => ({ ...s, show: false }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center px-4"
        data-testid="onboarding-nudge"
      >
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: "spring", stiffness: 220, damping: 22 }}
          className="bg-white rounded-2xl max-w-md w-full p-6 sm:p-7 relative shadow-2xl"
        >
          <button
            type="button"
            onClick={handleSkip}
            aria-label="Schließen"
            data-testid="onboarding-nudge-close"
            className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
          >
            <X className="w-5 h-5" />
          </button>

          <h3 className="text-xl font-extrabold tracking-tight text-[var(--fts-text-primary)]">
            Wem gehört dieser Flexpfad?
          </h3>
          <p className="mt-2 text-sm text-slate-600">
            Schön, dass du deine erster Flexpfad gespeichert hast! Damit dein PocketCoach
            sofort persönlich wird, kannst du 1–3 Tags wählen
            (oder später hinzufügen):
          </p>

          <div
            data-testid="onboarding-nudge-tags"
            className="mt-5 flex flex-wrap gap-2"
          >
            {state.tags.map((t, i) => {
              const active = picked.includes(t.key);
              return (
                <motion.button
                  key={t.key}
                  type="button"
                  onClick={() => togglePick(t.key)}
                  data-testid={`onboarding-nudge-tag-${t.key}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.15 + i * 0.05, type: "spring", stiffness: 200 }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold transition-colors border-2 ${
                    active
                      ? "border-[#4563BB] bg-[#4563BB] text-white"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                  }`}
                >
                  <span aria-hidden="true">{t.icon}</span>
                  <span>{t.label}</span>
                </motion.button>
              );
            })}
          </div>

          <p
            data-testid="onboarding-nudge-counter"
            className="mt-3 text-xs text-slate-400"
          >
            {picked.length} / 3 ausgewählt
          </p>

          <div className="mt-5 flex gap-2">
            <button
              type="button"
              onClick={handleSkip}
              data-testid="onboarding-nudge-later"
              className="flex-1 rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            >
              Später
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              data-testid="onboarding-nudge-save"
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-[#4563BB] hover:bg-[#3a55a5] disabled:opacity-50 text-white px-4 py-2 text-sm font-bold"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {picked.length === 0 ? "Auswählen" : "Speichern"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
